import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import './Pagination.css';

function Pagination({
  reduxSelector,
  reduxAction,
  totalPages,
  maxVisibleButtons = 5,
  pageSizeSelector,
  pageSizeAction,
  pageSize: directPageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) {
  const dispatch = useDispatch();
  const currentPage = useSelector(reduxSelector); // Pobranie bieżącej strony z Redux
  const selectedPageSize = useSelector((state) => {
    if (pageSizeSelector) {
      const val = pageSizeSelector(state);
      if (val !== undefined && val !== null && val !== '') {
        return Number(val) || 10;
      }
    }
    return directPageSize !== undefined && directPageSize !== null ? (Number(directPageSize) || 10) : 10;
  });

  const [isModalVisible, setIsModalVisible] = useState(false); // Czy modal jest widoczny
  const [inputValue, setInputValue] = useState(''); // Wartość wpisana w pole
  const [isPageSizeDropdownOpen, setIsPageSizeDropdownOpen] = useState(false);
  const pageSizeDropdownRef = useRef(null);

  // Close pageSize dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pageSizeDropdownRef.current && !pageSizeDropdownRef.current.contains(event.target)) {
        setIsPageSizeDropdownOpen(false);
      }
    };
    if (isPageSizeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPageSizeDropdownOpen]);

  // Funkcja do zmiany strony
  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      dispatch(reduxAction(page)); // Aktualizacja bieżącej strony w Redux
    }
  };

  const handlePageSizeSelect = (size) => {
    setIsPageSizeDropdownOpen(false);
    if (pageSizeAction) {
      dispatch(pageSizeAction(size));
    }
    if (onPageSizeChange) {
      onPageSizeChange(size);
    }
  };

  // Generowanie zakresu stron z obsługą "..."
  const generatePageButtons = () => {
    const buttons = [];
    const safeTotalPages = Math.max(1, totalPages || 1);
    const half = Math.floor(maxVisibleButtons / 2);

    if (safeTotalPages <= maxVisibleButtons) {
      for (let i = 1; i <= safeTotalPages; i++) {
        buttons.push(i);
      }
    } else {
      if (currentPage <= half + 1) {
        for (let i = 1; i <= maxVisibleButtons - 1; i++) {
          buttons.push(i);
        }
        buttons.push('...');
        buttons.push(safeTotalPages);
      } else if (currentPage >= safeTotalPages - half) {
        buttons.push(1);
        buttons.push('...');
        for (let i = safeTotalPages - (maxVisibleButtons - 2); i <= safeTotalPages; i++) {
          buttons.push(i);
        }
      } else {
        buttons.push(1);
        buttons.push('...');
        for (let i = currentPage - half + 1; i <= currentPage + half - 1; i++) {
          buttons.push(i);
        }
        buttons.push('...');
        buttons.push(safeTotalPages);
      }
    }

    return buttons;
  };

  const pageButtons = generatePageButtons();

  // Obsługa zatwierdzenia numeru strony
  const handleInputSubmit = () => {
    const page = parseInt(inputValue, 10);
    if (!isNaN(page)) {
      changePage(page);
    }
    setIsModalVisible(false); // Ukryj modal po zatwierdzeniu
    setInputValue(''); // Wyczyść pole
  };

  const hasPageSizeSelector = Boolean(pageSizeSelector || pageSizeAction || onPageSizeChange || directPageSize);

  return (
    <div className="pagination">
      <button
        type="button"
        className="pagination-button pagination-button-nav"
        disabled={currentPage <= 1}
        onClick={() => changePage(currentPage - 1)}
        aria-label="Poprzednia strona"
      >
        <ChevronLeft size={16} />
      </button>
      {pageButtons.map((button, index) =>
        button === '...' ? (
          <span key={`ellipsis-${index}`} className="pagination-ellipsis">
            ...
          </span>
        ) : (
          <button
            type="button"
            key={`page-${button}`}
            className={`pagination-button ${button === currentPage ? 'active' : ''}`}
            onClick={() => changePage(button)}
          >
            {button}
          </button>
        )
      )}
      <button
        type="button"
        className="pagination-button pagination-button-nav"
        disabled={currentPage >= totalPages}
        onClick={() => changePage(currentPage + 1)}
        aria-label="Następna strona"
      >
        <ChevronRight size={16} />
      </button>

      {/* Przycisk ze stroną */}
      <button
        type="button"
        className="pagination-button pagination-button-separated"
        onClick={() => setIsModalVisible(true)}
      >
        <span>Strona</span>
        <ChevronDown size={14} className="pagination-dropdown-icon" />
      </button>

      {/* Przycisk z rozmiarem strony (np. 10 / str.) */}
      {hasPageSizeSelector && (
        <div className="pagination-pagesize-container" ref={pageSizeDropdownRef}>
          <button
            type="button"
            className={`pagination-button pagination-button-separated pagination-pagesize-button ${isPageSizeDropdownOpen ? 'open' : ''}`}
            onClick={() => setIsPageSizeDropdownOpen((prev) => !prev)}
          >
            <span>{selectedPageSize} / str.</span>
            <ChevronDown
              size={14}
              className={`pagination-dropdown-icon ${isPageSizeDropdownOpen ? 'open' : ''}`}
            />
          </button>
          {isPageSizeDropdownOpen && (
            <div className="pagination-pagesize-dropdown">
              {pageSizeOptions.map((option) => (
                <div
                  key={option}
                  className={`pagination-pagesize-option ${option === selectedPageSize ? 'selected' : ''}`}
                  onClick={() => handlePageSizeSelect(option)}
                >
                  {option} na stronę
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {isModalVisible && (
        <div className="pagination-modal" onClick={() => setIsModalVisible(false)}>
          <div className="pagination-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Wybierz stronę</h3>
            <input
              type="number"
              className="pagination-input"
              placeholder="Nr strony"
              min="1"
              max={totalPages || 1}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInputSubmit()}
              autoFocus
            />
            <div className="pagination-modal-buttons">
              <button type="button" className="pagination-submit" onClick={handleInputSubmit}>
                OK
              </button>
              <button
                type="button"
                className="pagination-cancel"
                onClick={() => setIsModalVisible(false)}
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

Pagination.propTypes = {
  reduxSelector: PropTypes.func.isRequired, // Funkcja selektora Redux do pobrania bieżącej strony
  reduxAction: PropTypes.func.isRequired, // Akcja Redux do zmiany strony
  totalPages: PropTypes.number.isRequired, // Całkowita liczba stron
  maxVisibleButtons: PropTypes.number, // Maksymalna liczba widocznych przycisków (domyślnie 5)
  pageSizeSelector: PropTypes.func,
  pageSizeAction: PropTypes.func,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
};

export default Pagination;